/**
 * XSS Sanitization Utilities
 *
 * Uses isomorphic-dompurify for cross-platform HTML sanitization.
 * Works in both browser and Node.js environments.
 */

import DOMPurify from "isomorphic-dompurify";

const BLANK_TARGET_LINK_REGEX = /<a\s+([^>]*target=["']_blank["'][^>]*)>/gi;
const REL_ATTR_REGEX = /rel=/i;

/**
 * Sanitize input to plain text only.
 * Strips ALL HTML tags and attributes - safe for any text input.
 *
 * @param input - Raw user input
 * @returns Sanitized plain text string
 */
export function sanitizePlainText(input: string): string {
	return DOMPurify.sanitize(input, {
		ALLOWED_TAGS: [],
		ALLOWED_ATTR: [],
	});
}

/**
 * Sanitize input allowing only safe inline formatting.
 * Allows basic text formatting (bold, italic, links) but nothing else.
 * Automatically adds rel="noopener noreferrer" to links with target="_blank".
 *
 * @param input - Raw user input with potential rich text
 * @returns Sanitized HTML with only allowed formatting
 */
export function sanitizeRichText(input: string): string {
	const sanitized = DOMPurify.sanitize(input, {
		ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "br", "p"],
		ALLOWED_ATTR: ["href", "target", "rel"],
		FORCE_BODY: true,
	});

	// Enforce rel="noopener noreferrer" on links with target="_blank"
	// to prevent reverse-tabnabbing attacks
	return sanitized.replace(BLANK_TARGET_LINK_REGEX, (match, attrs) => {
		if (!REL_ATTR_REGEX.test(attrs)) {
			return `<a ${attrs} rel="noopener noreferrer">`;
		}
		return match;
	});
}

/**
 * Sanitize and trim a string.
 * Combines XSS sanitization with whitespace normalization.
 *
 * @param input - Raw user input
 * @returns Sanitized and trimmed string
 */
export function sanitizeAndTrim(input: string): string {
	return sanitizePlainText(input).trim();
}
